// ============================================================================
// CommandPalette - the global command bar (Cmd/Ctrl+K), staff shell only.
//
// Design round 3 (Oct 2026): the bar now DOES things, not just jumps.
//
//   Empty box     Recent patients (the last charts THIS user opened), Quick
//                 actions (Book a visit, New task, Check in a patient, New
//                 referral, Log touchpoint - only the ones the role can do;
//                 each asks for the patient next) and Go to (pages).
//   A name        Patients. The highlighted one gets a side panel (desktop)
//                 with its actions and one-key shortcuts: B book, M message,
//                 T task, I check in, R referral, L touchpoint, Enter chart.
//                 Tab moves into the panel. Phones list the actions under the
//                 tapped patient instead (44px rows, full-screen sheet).
//   A verb        "book jor", "message", "task", "refer", "check in": a chip
//                 shows the action and the list is who to apply it to
//                 (recent first; check in lists today's visits not yet in).
//   Any text      ends with "Ask the assistant: <text>", which opens the
//                 assistant drawer with the text in the box. Never sent.
//
// Every action opens the app's EXISTING entry point with the patient filled
// in - BookingModal, PortalMessageModal, TaskCreateModal, LogTouchpointModal,
// the Schedule visit (?date=&appt=, where Check in is one tap) and the chart's
// Referrals form - and is offered only when the role passes the same gate the
// destination uses (its nav entry, or referralsAuthsCore.canCreate).
//
// Privacy: search is the same RLS-scoped patients query as before (no
// practice filter needed; matching mirrors PatientsView). Recent patients are
// stored as patient uuids + times only (lib/commandBarCore.js); names are
// read back through that same RLS-scoped table. Nothing typed here goes in a
// URL, and the only ids in URLs are the chart's patient uuid and a visit id.
// ============================================================================
import { useState, useEffect, useRef, useMemo, useCallback, Suspense } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { C } from "../lib/tokens";
import { useAuth } from "../auth/AuthProvider";
import { useFeatureAccess } from "../hooks/useFeatureAccess";
import useIsMobile from "../hooks/useIsMobile";
import { useAssistantDrawer } from "./assistant/AssistantDrawerContext";
import { lazyRetry } from "../lib/chunkReload";
import { toast } from "./ToastProvider";
import { slotToTime } from "./constants";
import { canCreate as canCreateReferral } from "../lib/referralsAuthsCore";
import { quickActions as visitQuickActions, localYmd } from "../lib/scheduleCore";
import {
  verbFor, actionByKey, patientActions, quickActions, readRecentPatients, pushRecentPatient,
  patientIdFromPath, openedAgo, fmtDob,
} from "../lib/commandBarCore";
import {
  IconSearch, IconCalendar, IconMessage, IconCheckSquare, IconLogIn, IconSend, IconPhone,
  IconFileText, IconArrowRight, IconSparkles, IconX, IconRefresh,
} from "./icons";

// The existing forms, loaded on first use so the shell bundle stays lean.
const BookingModal = lazyRetry(() => import("./schedule/BookingModal"));
const PortalMessageModal = lazyRetry(() => import("./PortalMessageModal"));
const TaskCreateModal = lazyRetry(() => import("../views/tasks/TaskCreateModal"));
const LogTouchpointModal = lazyRetry(() => import("../views/care-management/LogTouchpointModal"));

const PATIENT_COLS = "id, first_name, last_name, mrn, date_of_birth, phone_mobile";
const VISIT_COLS = "id, patient_id, appt_date, start_slot, status, appt_type, checked_in_at, confirmed_at, confirmation_status";

const ICONS = {
  book: IconCalendar, message: IconMessage, task: IconCheckSquare, checkin: IconLogIn,
  referral: IconSend, touchpoint: IconPhone, chart: IconFileText,
};
// Section heading for the patient list while a verb is chosen.
const VERB_HEADING = {
  book: "Book for", message: "Message", task: "New task for", checkin: "Check in: visits today",
  referral: "Refer", touchpoint: "Log touchpoint for",
};
const VERB_PAGE = {
  book: "schedule", checkin: "queue", message: "inbox", task: "tasks", referral: "referrals", touchpoint: "care_management",
};

const initialsOf = (a, b) => ((a || "").charAt(0) + (b || "").charAt(0)).toUpperCase() || "?";
const nameOf = (p) => ((p.first_name || "") + " " + (p.last_name || "")).trim() || "Patient";
const metaOf = (p) => [p.date_of_birth ? "DOB " + fmtDob(p.date_of_birth) : null, "MRN " + (p.mrn || "-")].filter(Boolean).join(" - ");
const visitLabel = (a) => "Visit today " + (Number.isFinite(a.start_slot) ? slotToTime(a.start_slot) : "");
const storage = () => { try { return window.localStorage; } catch (e) { return null; } };

export default function CommandPalette({ open, onClose, pages = [], actions = [], onNavigate }) {
  const { profile, role, practiceId, tier } = useAuth();
  const { can } = useFeatureAccess();
  const drawer = useAssistantDrawer();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const userId = profile && profile.id;

  const [q, setQ] = useState("");
  const [pendingVerb, setPendingVerb] = useState(null);
  const [active, setActive] = useState(0);
  const [hover, setHover] = useState(-1);
  const [expandedId, setExpandedId] = useState(null);
  const [search, setSearch] = useState({ rows: [], loading: false, error: null });
  const [searchTry, setSearchTry] = useState(0);
  const [recent, setRecent] = useState({ list: [], rows: [], loading: false, error: null });
  const [recentTry, setRecentTry] = useState(0);
  const [visits, setVisits] = useState({ key: "", byPatient: {}, loading: false, error: null });
  const [visitsTry, setVisitsTry] = useState(0);
  const [arrivals, setArrivals] = useState({ rows: [], loading: false, error: null });
  const [arrivalsTry, setArrivalsTry] = useState(0);
  const [modal, setModal] = useState(null);
  const [staff, setStaff] = useState([]);
  const inputRef = useRef(null);
  const panelRef = useRef(null);

  // The role's own gates, exactly as the destinations apply them: the nav
  // entries (already filtered by NAV_BY_ROLE and care-management access in
  // Layout), referralsAuthsCore.canCreate plus the chart's Pro tier gate for
  // its Referrals section, and the assistant's feature predicate.
  const pageIds = pages.map((p) => p.id).join(",");
  const perms = useMemo(() => {
    const ids = new Set(pageIds ? pageIds.split(",") : []);
    return {
      book: ids.has("schedule"),
      checkIn: ids.has("schedule"),
      message: ids.has("inbox"),
      task: ids.has("tasks"),
      referral: canCreateReferral(role) && ["Pro", "Command"].includes(tier),
      touchpoint: ids.has("care_management"),
      ask: !!drawer && !!(can && can.aiAssistant && can.aiAssistant()),
    };
  }, [pageIds, role, tier, drawer, can]);

  // Remember every chart this user opens (from anywhere, not only from here).
  useEffect(() => {
    const id = patientIdFromPath(location.pathname);
    if (id && userId) pushRecentPatient(storage(), userId, id);
  }, [location.pathname, userId]);

  // Reset and focus each time the bar opens.
  useEffect(() => {
    if (!open) return undefined;
    setQ(""); setPendingVerb(null); setActive(0); setHover(-1); setExpandedId(null);
    setSearch({ rows: [], loading: false, error: null });
    const t = setTimeout(() => inputRef.current && inputRef.current.focus(), 20);
    return () => clearTimeout(t);
  }, [open]);

  const qt = q.trim();
  const verb = pendingVerb ? { verb: pendingVerb, rest: qt } : verbFor(q, perms);
  const verbKey = verb ? verb.verb : null;
  const nameQ = verb ? verb.rest : qt;
  const isArrivals = verbKey === "checkin";
  const showRecents = !!open && ((!qt && !verb) || (!!verb && !nameQ && !isArrivals));

  // Debounced patient search. RLS scopes rows to the practice.
  useEffect(() => {
    if (!open || isArrivals) return undefined;
    const term = nameQ;
    if (term.length < 2) { setSearch({ rows: [], loading: false, error: null }); return undefined; }
    let cancelled = false;
    setSearch((s) => ({ ...s, loading: true, error: null }));
    const h = setTimeout(async () => {
      // Strip characters that would break the PostgREST or() filter syntax.
      const esc = term.replace(/[%,()]/g, " ");
      const { data, error } = await supabase.from("patients")
        .select(PATIENT_COLS)
        .or(`first_name.ilike.%${esc}%,last_name.ilike.%${esc}%,mrn.ilike.%${esc}%,phone_mobile.ilike.%${esc}%`)
        .order("last_name").limit(8);
      if (cancelled) return;
      setSearch(error ? { rows: [], loading: false, error: "Couldn't search patients." } : { rows: data || [], loading: false, error: null });
    }, 220);
    return () => { cancelled = true; clearTimeout(h); };
  }, [nameQ, open, isArrivals, searchTry]);

  // Recent patients: ids from this browser, names from the RLS-scoped table.
  useEffect(() => {
    if (!showRecents) return undefined;
    const list = readRecentPatients(storage(), userId);
    if (!list.length) { setRecent({ list: [], rows: [], loading: false, error: null }); return undefined; }
    let cancelled = false;
    setRecent((r) => ({ ...r, list, loading: true, error: null }));
    supabase.from("patients").select(PATIENT_COLS).in("id", list.map((r) => r.id))
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) { setRecent({ list, rows: [], loading: false, error: "Couldn't load recent patients." }); return; }
        const byId = new Map((data || []).map((p) => [p.id, p]));
        // Keep the opened order; a chart the user can no longer see drops out.
        const rows = list.map((r) => byId.get(r.id) && { ...byId.get(r.id), _at: r.at }).filter(Boolean);
        setRecent({ list, rows, loading: false, error: null });
      });
    return () => { cancelled = true; };
  }, [showRecents, userId, recentTry]);

  // "Check in" as a verb lists today's visits that can still be checked in.
  useEffect(() => {
    if (!open || !isArrivals) return undefined;
    let cancelled = false;
    setArrivals((a) => ({ ...a, loading: true, error: null }));
    supabase.from("appointments")
      .select(VISIT_COLS + ", patients(" + PATIENT_COLS + ")")
      .eq("appt_date", localYmd()).in("status", ["Scheduled", "Confirmed"])
      .order("start_slot").limit(60)
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) { setArrivals({ rows: [], loading: false, error: "Couldn't load today's visits." }); return; }
        const rows = (data || [])
          .filter((a) => a.patients && visitQuickActions(a).includes("check_in"))
          .map((a) => ({ ...a.patients, _visit: a }));
        setArrivals({ rows, loading: false, error: null });
      });
    return () => { cancelled = true; };
  }, [open, isArrivals, arrivalsTry]);

  // ---- Sections ---------------------------------------------------------------
  const nq = nameQ.toLowerCase();
  const patientRows = isArrivals
    ? arrivals.rows.filter((p) => !nq || nameOf(p).toLowerCase().includes(nq) || String(p.mrn || "").toLowerCase().includes(nq))
    : showRecents ? recent.rows : search.rows;

  // Today's visit for every patient on screen, so Check in is offered only
  // when there is one to check in. One read per result set.
  const visitIds = isArrivals || !perms.checkIn ? "" : patientRows.map((p) => p.id).join(",");
  useEffect(() => {
    if (!open || !visitIds) return undefined;
    let cancelled = false;
    setVisits((v) => ({ ...v, loading: true, error: null }));
    supabase.from("appointments").select(VISIT_COLS)
      .in("patient_id", visitIds.split(",")).eq("appt_date", localYmd()).order("start_slot")
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) { setVisits({ key: visitIds, byPatient: {}, loading: false, error: "Couldn't check today's visits." }); return; }
        const byPatient = {};
        for (const a of data || []) (byPatient[a.patient_id] = byPatient[a.patient_id] || []).push(a);
        setVisits({ key: visitIds, byPatient, loading: false, error: null });
      });
    return () => { cancelled = true; };
  }, [open, visitIds, visitsTry]);

  const todayFor = useCallback((p) => {
    if (!p) return { checkable: null, visit: null };
    if (p._visit) return { checkable: p._visit, visit: p._visit };
    const list = (visits.key === visitIds && visits.byPatient[p.id]) || [];
    const checkable = list.find((a) => visitQuickActions(a).includes("check_in")) || null;
    const visit = checkable || list.find((a) => a.status !== "Cancelled" && a.status !== "Rescheduled") || null;
    return { checkable, visit };
  }, [visits, visitIds]);

  const actionsFor = useCallback((p) => patientActions(perms, { canCheckInToday: !!todayFor(p).checkable }), [perms, todayFor]);

  const pageMatches = useMemo(() => {
    const term = qt.toLowerCase();
    if (!term) return pages.slice(0, 5);
    return pages.filter((p) => p.label.toLowerCase().includes(term)).slice(0, 5);
  }, [qt, pages]);

  // Existing actions (pages that open forms, settings, ...) are matched on
  // label AND keywords, so "new touchpoint" still finds "Log touchpoint".
  const actionMatches = useMemo(() => {
    const term = qt.toLowerCase();
    if (!term) return [];
    return actions
      .filter((a) => a.label.toLowerCase().includes(term) || (a.keywords || "").toLowerCase().includes(term))
      .slice(0, 5);
  }, [qt, actions]);

  const quick = quickActions(perms);
  const sections = [];
  const patientStatus = isArrivals ? arrivals : showRecents ? recent : search;
  const retryPatients = isArrivals ? () => setArrivalsTry((n) => n + 1) : showRecents ? () => setRecentTry((n) => n + 1) : () => setSearchTry((n) => n + 1);
  const patientSub = (p) => (p._visit ? visitLabel(p._visit) + (p._visit.appt_type ? " - " + p._visit.appt_type : "")
    : showRecents && p._at ? openedAgo(p._at) : metaOf(p));
  const patientHeading = verb
    ? VERB_HEADING[verbKey] + (showRecents ? " (recent)" : "")
    : showRecents ? "Recent patients" : "Patients";
  const patientSection = { key: "patients", label: patientHeading, status: patientStatus, retry: retryPatients,
    rows: patientRows.map((p) => ({ kind: "patient", id: "pt-" + p.id, p })) };

  if (!qt && !verb) {
    if (recent.rows.length || recent.loading || recent.error) sections.push(patientSection);
    if (quick.length) sections.push({ key: "quick", label: "Quick actions", rows: quick.map((a) => ({ kind: "verb", id: "qa-" + a.key, a })) });
    if (pageMatches.length) sections.push({ key: "pages", label: "Go to", rows: pageMatches.map((p) => ({ kind: "page", id: "pg-" + p.id, page: p })) });
  } else {
    sections.push(patientSection);
    const verbRows = verb ? [] : quick
      .filter((a) => a.label.toLowerCase().includes(qt.toLowerCase()))
      .map((a) => ({ kind: "verb", id: "qa-" + a.key, a }));
    const actRows = actionMatches.map((a) => ({ kind: "action", id: "ac-" + a.id, action: a }));
    if (verbRows.length || actRows.length) sections.push({ key: "actions", label: "Actions", rows: [...verbRows, ...actRows] });
    // With a verb chosen, Go to offers only the page that action lives on
    // (Book a visit -> Schedule), not the whole nav.
    const goTo = verb ? pages.filter((p) => p.id === VERB_PAGE[verbKey]) : pageMatches;
    if (goTo.length) sections.push({ key: "pages", label: "Go to", rows: goTo.map((p) => ({ kind: "page", id: "pg-" + p.id, page: p })) });
  }
  if (qt && perms.ask) sections.push({ key: "ask", label: "Assistant", rows: [{ kind: "ask", id: "ask" }] });

  const items = sections.flatMap((s) => s.rows);
  const itemKey = items.map((it) => it.id).join("|");

  // Keep the highlight in range as results change; a new query starts at the top.
  useEffect(() => { setActive(0); setExpandedId(null); }, [q, pendingVerb]);
  useEffect(() => { if (active > items.length - 1) setActive(0); }, [itemKey, active, items.length]);

  const activeItem = items[active] || null;
  const panelPatient = !isMobile && !verb && activeItem && activeItem.kind === "patient" ? activeItem.p : null;

  // ---- Running things ---------------------------------------------------------
  const focusInput = () => { if (inputRef.current) inputRef.current.focus(); };

  const runAction = (key, p) => {
    if (!p) return;
    const name = nameOf(p);
    if (key === "chart") { onNavigate("/patients/" + p.id); return; }
    if (key === "checkin") {
      // The Schedule opens this visit's details, where Check in is one tap
      // (the same front desk action, same RPC, same audit).
      const v = todayFor(p).checkable;
      if (!v) return;
      onNavigate("/schedule?date=" + v.appt_date + "&appt=" + v.id);
      return;
    }
    if (key === "referral") {
      // The chart's Referrals section opens its New referral form from
      // navigation state (ReferralsTab), so nothing extra rides in the URL.
      onClose();
      navigate("/patients/" + p.id + "/referrals", { state: { newReferral: true } });
      return;
    }
    onClose();
    setModal({ kind: key, patient: { id: p.id, first_name: p.first_name, last_name: p.last_name, date_of_birth: p.date_of_birth, mrn: p.mrn }, name });
  };

  const select = (it, how) => {
    if (!it) return;
    if (it.kind === "patient") {
      if (verb) { runAction(verbKey, it.p); return; }
      if (how === "enter") { runAction("chart", it.p); return; }
      if (isMobile) { setExpandedId((id) => (id === it.p.id ? null : it.p.id)); return; }
      setActive(items.indexOf(it));
      return;
    }
    if (it.kind === "verb") { setPendingVerb(it.a.key); setQ(""); focusInput(); return; }
    if (it.kind === "action") { onClose(); it.action.run(); return; }
    if (it.kind === "page") { onNavigate(it.page.path); return; }
    if (it.kind === "ask") {
      // Prefill only. The user reads the question and presses Ask.
      onClose();
      drawer.openWithDraft(qt);
    }
  };

  const focusPanel = (i) => {
    const els = panelRef.current ? Array.from(panelRef.current.querySelectorAll("button[data-cb-action]")) : [];
    if (!els.length) return false;
    els[(i + els.length) % els.length].focus();
    return true;
  };

  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      if (pendingVerb && !q) { setPendingVerb(null); return; }
      onClose();
    } else if (e.key === "Backspace" && !q && pendingVerb) {
      e.preventDefault(); setPendingVerb(null);
    } else if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, items.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); select(activeItem, "enter"); }
    else if (e.key === "Tab" && !e.shiftKey) {
      // Tab moves into the highlighted patient's actions; focus otherwise
      // stays in the box (the bar is modal).
      e.preventDefault();
      if (panelPatient) focusPanel(0);
    } else if (e.key === "Tab") e.preventDefault();
  };

  const onPanelKeyDown = (e, list, p) => {
    const btns = panelRef.current ? Array.from(panelRef.current.querySelectorAll("button[data-cb-action]")) : [];
    const at = btns.indexOf(document.activeElement);
    const hit = list.find((a) => a.hotkey.length === 1 && a.hotkey.toLowerCase() === String(e.key).toLowerCase());
    const stop = () => { e.preventDefault(); e.stopPropagation(); };
    if (hit && !e.metaKey && !e.ctrlKey && !e.altKey) { stop(); runAction(hit.key, p); }
    else if (e.key === "ArrowDown") { stop(); focusPanel(at + 1); }
    else if (e.key === "ArrowUp") { stop(); focusPanel(at - 1); }
    else if (e.key === "Escape" || e.key === "ArrowLeft" || (e.key === "Tab" && e.shiftKey && at <= 0)) { stop(); focusInput(); }
    else if (e.key === "Tab" && !e.shiftKey && at >= btns.length - 1) { stop(); focusInput(); }
  };

  // Staff list for the task form's assignee picker, loaded when it opens.
  const taskOpen = modal && modal.kind === "task";
  useEffect(() => {
    if (!taskOpen) return undefined;
    let live = true;
    import("../lib/tasksApi").then((m) => m.fetchStaff())
      .then((rows) => { if (live) setStaff(rows || []); })
      .catch(() => { if (live) toast({ kind: "warn", text: "Couldn't load the staff list. You can still assign the task to a team queue." }); });
    return () => { live = false; };
  }, [taskOpen]);

  const closeModal = () => setModal(null);
  const modalEl = modal && (
    <Suspense fallback={null}>
      {modal.kind === "book" && (
        <BookingModal title={"Book " + modal.name}
          initial={{ patient_id: modal.patient.id, patients: modal.patient }}
          lockPatient onClose={closeModal}
          onSaved={() => { closeModal(); toast({ kind: "success", text: "Visit booked." }); }} />
      )}
      {modal.kind === "message" && (
        <PortalMessageModal patientId={modal.patient.id} patientName={modal.name} practiceId={practiceId} me={profile}
          onClose={closeModal} onSent={() => { closeModal(); toast({ kind: "success", text: "Message sent." }); }} />
      )}
      {modal.kind === "task" && (
        <TaskCreateModal practiceId={practiceId} me={profile} myRole={role} staff={staff}
          defaults={{ patient: modal.patient }}
          onClose={closeModal} onCreated={() => { closeModal(); toast({ kind: "success", text: "Task created." }); }} />
      )}
      {modal.kind === "touchpoint" && (
        <LogTouchpointModal practiceId={practiceId} userId={userId} userRole={role}
          initialPatientId={modal.patient.id} lockPatient
          onClose={closeModal} onLogged={() => { closeModal(); toast({ kind: "success", text: "Touchpoint logged." }); }} />
      )}
    </Suspense>
  );

  if (!open) return modalEl || null;

  // ---- Rendering --------------------------------------------------------------
  const chipAction = verb ? actionByKey(verbKey) : null;
  const kbd = (t) => (
    <kbd style={{
      fontSize: 11, color: C.textTertiary, border: "1px solid " + C.borderLight, borderRadius: 4,
      padding: "1px 6px", flexShrink: 0, fontFamily: "inherit", background: C.bgPrimary, lineHeight: "16px",
    }}>{t}</kbd>
  );
  const tile = (Icon, tone) => (
    <span aria-hidden="true" style={{
      width: 30, height: 30, borderRadius: 8, flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center",
      background: tone === "ai" ? C.aiBg : C.bgSecondary, color: tone === "ai" ? C.aiFg : C.brandText,
    }}><Icon size={15} /></span>
  );
  const avatar = (p, size) => (
    <span aria-hidden="true" style={{
      width: size, height: size, borderRadius: "50%", flexShrink: 0, background: C.teal,
      color: "var(--brand-contrast, " + C.onAccent + ")", display: "inline-flex", alignItems: "center", justifyContent: "center",
      fontSize: size > 36 ? 14 : 11, fontWeight: 700,
    }}>{initialsOf(p.first_name, p.last_name)}</span>
  );
  const rowStyle = (isActive, isHover) => ({
    display: "flex", alignItems: "center", gap: 12, width: "100%", minHeight: 44, padding: "6px 10px",
    border: "none", borderRadius: 8, textAlign: "left", fontFamily: "inherit", cursor: "pointer",
    background: isActive ? C.tealBg : isHover ? C.bgSecondary : "transparent", color: C.textPrimary,
    boxShadow: isActive ? "inset 3px 0 0 " + C.teal : "none",
  });
  const sectionLabel = (t) => (
    <div style={{ padding: "10px 10px 4px", fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: C.textTertiary }}>{t}</div>
  );
  const statusLine = (text, retry, tone) => (
    <div role={tone === "error" ? "alert" : "status"} style={{
      display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", fontSize: 12,
      color: tone === "error" ? C.dangerFg : C.textTertiary,
    }}>
      <span style={{ flex: 1 }}>{text}</span>
      {retry && (
        <button type="button" onClick={retry} style={{
          display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, fontWeight: 600, fontFamily: "inherit",
          border: "1px solid " + C.borderMid, background: C.bgPrimary, color: C.textPrimary, borderRadius: 6,
          padding: "0 10px", minHeight: isMobile ? 44 : 28, cursor: "pointer",
        }}><IconRefresh size={12} />Retry</button>
      )}
    </div>
  );

  // One action row (desktop panel and phone expansion share it).
  const actionButton = (a, p, list, phone) => {
    const Icon = ICONS[a.key];
    return (
      <button key={a.key} type="button" data-cb-action={a.key}
        onClick={() => runAction(a.key, p)}
        onKeyDown={phone ? undefined : (e) => onPanelKeyDown(e, list, p)}
        style={{
          display: "flex", alignItems: "center", gap: 10, width: "100%", minHeight: phone ? 44 : 40,
          padding: phone ? "0 10px 0 52px" : "0 10px", border: "none", borderRadius: 8, background: "transparent",
          color: C.textPrimary, fontSize: 14, fontWeight: 600, fontFamily: "inherit", textAlign: "left", cursor: "pointer",
        }}
        onFocus={(e) => { e.currentTarget.style.background = C.tealBg; }}
        onBlur={(e) => { e.currentTarget.style.background = "transparent"; }}>
        <span aria-hidden="true" style={{ display: "inline-flex", color: C.brandText }}><Icon size={16} /></span>
        <span style={{ flex: 1 }}>{a.label}</span>
        {!phone && kbd(a.hotkey)}
      </button>
    );
  };

  let idx = -1;
  const renderRow = (it) => {
    idx++; const i = idx; const isActive = i === active; const isHover = i === hover;
    const common = {
      id: "cb-opt-" + i, role: "option", "aria-selected": isActive,
      onMouseEnter: () => setHover(i), onMouseLeave: () => setHover(-1),
      onClick: () => select(it, "click"),
    };
    if (it.kind === "patient") {
      const p = it.p;
      const expanded = isMobile && !verb && expandedId === p.id;
      const list = expanded ? actionsFor(p) : null;
      return (
        <div key={it.id}>
          <button type="button" {...common} data-cb-patient={p.id} aria-expanded={isMobile && !verb ? expanded : undefined} style={rowStyle(isActive || expanded, isHover)}>
            {avatar(p, 30)}
            <span style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
              <span style={{ fontSize: 14, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{nameOf(p)}</span>
              <span style={{ fontSize: 12, color: C.textSecondary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{patientSub(p)}</span>
            </span>
            {!isMobile && isActive && kbd(verb ? "Enter" : "Tab")}
          </button>
          {expanded && (
            <div role="group" aria-label={"Actions for " + nameOf(p)} style={{ padding: "2px 0 6px" }}>
              {todayFor(p).visit && (
                <div style={{ padding: "0 10px 4px 52px", fontSize: 12, color: C.successFg, fontWeight: 600 }}>{visitLabel(todayFor(p).visit)}</div>
              )}
              {perms.checkIn && visits.error && statusLine(visits.error, () => setVisitsTry((n) => n + 1), "error")}
              {list.map((a) => actionButton(a, p, list, true))}
            </div>
          )}
        </div>
      );
    }
    if (it.kind === "verb") {
      const Icon = ICONS[it.a.key];
      return (
        <button key={it.id} type="button" {...common} style={rowStyle(isActive, isHover)}>
          {tile(Icon)}
          <span style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
            <span style={{ fontSize: 14, fontWeight: 600 }}>{it.a.label}</span>
            <span style={{ fontSize: 12, color: C.textSecondary }}>Pick the patient next</span>
          </span>
        </button>
      );
    }
    if (it.kind === "action") {
      const a = it.action;
      return (
        <button key={it.id} type="button" {...common} style={rowStyle(isActive, isHover)}>
          <span aria-hidden="true" style={{
            width: 30, height: 30, borderRadius: 8, flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center",
            background: C.bgSecondary, color: C.brandText, fontSize: 13,
          }}>{a.icon || <IconArrowRight size={15} />}</span>
          <span style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
            <span style={{ fontSize: 14, fontWeight: 600 }}>{a.label}</span>
            {a.hint && <span style={{ fontSize: 12, color: C.textSecondary }}>{a.hint}</span>}
          </span>
        </button>
      );
    }
    if (it.kind === "page") {
      return (
        <button key={it.id} type="button" {...common} style={rowStyle(isActive, isHover)}>
          {tile(IconArrowRight)}
          <span style={{ fontSize: 14, fontWeight: 600, flex: 1 }}>{it.page.label}</span>
        </button>
      );
    }
    // Ask the assistant: the one purple row (AI styling only here).
    return (
      <button key={it.id} type="button" {...common} data-cb-ask="1" style={{ ...rowStyle(false, isHover), background: isActive ? C.aiBg : isHover ? C.bgSecondary : "transparent", boxShadow: isActive ? "inset 3px 0 0 " + C.aiSolid : "none" }}>
        {tile(IconSparkles, "ai")}
        <span style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: C.aiFg, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{"Ask the assistant: \"" + qt + "\""}</span>
          <span style={{ fontSize: 12, color: C.textSecondary }}>Opens the assistant with this question. Nothing is sent until you press Ask.</span>
        </span>
      </button>
    );
  };

  const panelActions = panelPatient ? actionsFor(panelPatient) : [];
  const panelToday = panelPatient ? todayFor(panelPatient) : null;
  const panel = panelPatient && (
    <aside ref={panelRef} aria-label={"Actions for " + nameOf(panelPatient)} style={{
      flex: "0 0 300px", borderLeft: "1px solid " + C.borderLight, background: C.bgSecondary,
      padding: 16, display: "flex", flexDirection: "column", gap: 12, overflowY: "auto",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
        {avatar(panelPatient, 44)}
        <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
          <span style={{ fontSize: 16, fontWeight: 700, color: C.textPrimary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{nameOf(panelPatient)}</span>
          <span style={{ fontSize: 12, color: C.textSecondary }}>{metaOf(panelPatient)}</span>
        </div>
      </div>
      {perms.checkIn && (
        visits.loading && visits.key !== visitIds ? <span style={{ fontSize: 12, color: C.textTertiary }}>Checking today's visits...</span>
          : visits.error ? statusLine(visits.error, () => setVisitsTry((n) => n + 1), "error")
          : panelToday.visit ? (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 700, padding: "2px 8px", borderRadius: 6, background: C.successBg, color: C.successFg }}>
                {visitLabel(panelToday.visit) + (panelToday.checkable ? "" : " - " + (panelToday.visit.checked_in_at ? "checked in" : String(panelToday.visit.status || "").toLowerCase()))}
              </span>
            </div>
          ) : null
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {panelActions.map((a) => actionButton(a, panelPatient, panelActions, false))}
      </div>
    </aside>
  );

  // Portalled to document.body: position:fixed resolves against any ancestor
  // with a transform/filter, so an animated wrapper would clip this overlay.
  const overlay = (
    <div
      onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{
        position: "fixed", inset: 0, zIndex: 400, background: C.scrim,
        display: "flex", alignItems: "flex-start", justifyContent: "center",
        paddingTop: isMobile ? 0 : "10vh", animation: "posFadeIn 0.14s ease",
      }}
    >
      <div role="dialog" aria-modal="true" aria-label="Command bar" style={{
        width: isMobile ? "100%" : "min(820px, calc(100vw - 32px))",
        height: isMobile ? "100dvh" : "auto", maxHeight: isMobile ? "100dvh" : "76vh",
        background: C.bgPrimary, borderRadius: isMobile ? 0 : 12,
        boxShadow: C.shadowLg, overflow: "hidden", display: "flex", flexDirection: "column",
        animation: "posModalIn 0.16s cubic-bezier(0.16,1,0.3,1)",
      }}>
        {/* Search box, with the chosen action as a chip */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, minHeight: 56, padding: isMobile ? "6px 6px 6px 14px" : "0 16px", borderBottom: "1px solid " + C.borderLight }}>
          <span style={{ display: "inline-flex", color: C.textTertiary, flexShrink: 0 }}><IconSearch size={17} /></span>
          {chipAction && (
            <span data-cb-chip="1" style={{
              display: "inline-flex", alignItems: "center", gap: 4, height: 28, padding: "0 4px 0 10px", borderRadius: 999,
              background: C.tealBg, color: C.brandText, fontSize: 12, fontWeight: 700, whiteSpace: "nowrap", flexShrink: 0,
            }}>
              {chipAction.chip}
              <button type="button" aria-label="Clear action" onClick={() => { setPendingVerb(null); setQ(verb && !pendingVerb ? verb.rest : q); focusInput(); }}
                style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 22, height: 22, border: "none", borderRadius: 999, background: "transparent", color: "inherit", cursor: "pointer", padding: 0 }}>
                <IconX size={12} />
              </button>
            </span>
          )}
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            role="combobox" aria-expanded="true" aria-controls="cb-results" aria-autocomplete="list"
            aria-activedescendant={activeItem ? "cb-opt-" + active : undefined}
            aria-label="Search patients, actions and pages"
            placeholder={verb ? "Type a patient's name" : "Search a patient, or type what you want to do"}
            style={{
              flex: 1, minWidth: 0, border: "none", outline: "none", background: "transparent",
              fontSize: 16, fontFamily: "inherit", color: C.textPrimary, minHeight: 44,
              // The whole bar is the field; the app-wide focus ring would draw
              // a second box inside it.
              boxShadow: "none",
            }}
          />
          {isMobile ? (
            <button type="button" onClick={onClose} aria-label="Close" style={{
              width: 44, height: 44, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              border: "none", background: "transparent", color: C.textSecondary, cursor: "pointer", borderRadius: 8,
            }}><IconX size={18} /></button>
          ) : kbd("Esc")}
        </div>

        <div style={{ display: "flex", flex: 1, minHeight: isMobile ? 0 : 360 }}>
          <div id="cb-results" role="listbox" aria-label="Results" style={{ flex: "1 1 auto", minWidth: 0, overflowY: "auto", padding: 8 }}>
            {sections.map((s) => (
              <div key={s.key} role="group" aria-label={s.label}>
                {sectionLabel(s.label)}
                {s.rows.map(renderRow)}
                {s.status && s.status.error && statusLine(s.status.error, s.retry, "error")}
                {s.status && s.status.loading && s.rows.length === 0 && statusLine(isArrivals ? "Loading today's visits..." : "Searching patients...")}
                {s.key === "patients" && !s.status.loading && !s.status.error && s.rows.length === 0 && (
                  statusLine(isArrivals ? (nameQ ? "No visit today for \"" + nameQ + "\" waiting to be checked in." : "No visits waiting to be checked in today.")
                    : showRecents ? "No recent patients yet. Type a name to search."
                    : nameQ.length < 2 ? "Type at least 2 letters of a name or MRN." : "No patients match \"" + nameQ + "\".")
                )}
              </div>
            ))}
          </div>
          {panel}
        </div>

        {!isMobile && (
          <div style={{
            borderTop: "1px solid " + C.borderLight, padding: "0 16px", minHeight: 36,
            fontSize: 12, color: C.textTertiary, display: "flex", alignItems: "center", gap: 16,
          }}>
            <span>Up / Down to move</span>
            <span>{verb ? "Enter to " + (chipAction.chip || "run").toLowerCase() : "Enter to open"}</span>
            {panelPatient && <span>Tab for actions</span>}
            {pendingVerb && <span>Backspace to clear the action</span>}
          </div>
        )}
      </div>
    </div>
  );
  return (
    <>
      {typeof document !== "undefined" ? createPortal(overlay, document.body) : overlay}
      {modalEl}
    </>
  );
}
